/** @jsxImportSource @emotion/react */
import {css} from "@emotion/react";
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import moment from "moment";
import {IconButton, Menu, MenuItem} from "@mui/material";
import * as Immutable from "immutable";
import {RRule} from "rrule";
import * as ScheduledReports from "js/common/scheduled-reports";
import * as SavedConfigs from "js/common/saved-configs";
import * as Auditor from "js/common/auditor";
import * as Users from "js/common/users";
import {indexBy} from "js/common/utils/collections";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import ScheduleDrawer from "js/dashboards/scheduling/schedule-drawer";
import SimpleDataTable from "js/common/views/tables/simple-data-table";
import LoadingSpinner from "js/common/views/loading-spinner";
import Dialog from "js/onpoint/dialog";
import {TextButton} from "js/common/views/inputs/buttons";
import * as Popups from "js/common/popups";

const formatDateTime = (dateTimeString, pattern = "MMM D, YYYY h:mm A") => {
  if (!dateTimeString) {
    return "-";
  }
  return moment(dateTimeString).format(pattern);
};

// Intl.DateTimeFormat is used instead of moment because we don't use moment-timezone,
// and plain moment cannot format a date in an arbitrary timezone (e.g. "America/New_York").
const formatDateTimeInTimezone = (isoString, timezone) => {
  if (!isoString) return "-";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short"
  }).format(new Date(isoString));
};

const getRecurrenceText = (rruleString) => {
  if (!rruleString) {
    return "One-time";
  }

  const freqMatch = rruleString.match(/FREQ=(\w+)/);

  if (!freqMatch) {
    return "One-time";
  }

  const freq = freqMatch[1];

  const frequencyMap = {
    DAILY: "Daily",
    WEEKLY: "Weekly",
    MONTHLY: "Monthly",
    YEARLY: "Yearly",
    HOURLY: "Hourly"
  };

  return frequencyMap[freq] || "Custom";
};

// Convert a local date/time in a specific timezone to a UTC Date
const localTimeToUTC = (dateStr, timeStr, timezone) => {
  // Create a Date interpreting the given date/time as if in UTC
  const fakeUtcMs = Date.parse(`${dateStr}T${timeStr}Z`);
  const fakeUtcDate = new Date(fakeUtcMs);

  // Calculate the offset between UTC and the target timezone at this time
  const utcFormatted = fakeUtcDate.toLocaleString("en-US", {timeZone: "UTC"});
  const tzFormatted = fakeUtcDate.toLocaleString("en-US", {timeZone: timezone});
  const offsetMs = new Date(utcFormatted).getTime() - new Date(tzFormatted).getTime();

  return new Date(fakeUtcMs + offsetMs);
};

const formatUTCForRRule = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}Z`;
};

const getNextScheduledRun = (report) => {
  const rruleString = report.get("recurrenceSchedule");
  const startDate = report.get("localStartDate");
  const startTime = report.get("localStartTime");
  const timezone = report.get("timezone");

  if (!rruleString || !startDate || !startTime) {
    return null;
  }

  try {
    // Convert local start time in the report's timezone to UTC for DTSTART
    const dtstartUtc = timezone
        ? localTimeToUTC(startDate, startTime, timezone)
        : new Date(`${startDate}T${startTime}`);

    // Create RRule with the correct UTC DTSTART
    const rule = RRule.fromString(`DTSTART:${formatUTCForRRule(dtstartUtc)}\nRRULE:${rruleString}`);

    // Get next occurrence after now
    const now = new Date();
    const nextDate = rule.after(now, true);

    if (nextDate && timezone) {
      // RRule returns dates anchored to the original DTSTART UTC offset,
      // which is wrong after DST boundaries. Re-interpret: take the date
      // from RRule but recompute the correct UTC time for that specific date.
      const occurrenceDateStr = `${nextDate.getUTCFullYear()}-${String(nextDate.getUTCMonth() + 1).padStart(2, "0")}-${String(nextDate.getUTCDate()).padStart(2, "0")}`;
      const corrected = localTimeToUTC(occurrenceDateStr, startTime, timezone);
      return corrected.toISOString();
    } else if (nextDate) {
      return nextDate.toISOString();
    }
    return null;
  } catch (e) {
    console.error("Error calculating next scheduled run:", e);
    return null;
  }
};

const getStyles = (theme) => ({
  iconButton: css`
    padding: 0px;
    font-size: 14px;
  `,
  searchContainer: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    padding: 0;
  `,
  searchInput: css`
    border-radius: 0.2rem;
    border: 1px solid ${theme.palette.border.main};
    font-size: 0.85rem;
    color: ${theme.palette.text.main};
    background-color: ${theme.palette.background.card};
    padding: 7px 12px;
    width: 15%;
    min-width: 340px;
    font-family: ${theme.typography.fontFamily};
    display: flex;
    align-items: center;

    input {
      font-size: 14px;
      border: none;
      width: 100%;
      padding-left: 10px;
      background-color: ${theme.palette.background.card};
      color: ${theme.palette.text.main};
      font-family: ${theme.typography.fontFamily};
      outline: none;
    }
  `
});

function ActionMenu({rowId, onEdit, onClone, onDelete}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const {theme} = useContext(CustomThemeContext);
  const open = Boolean(anchorEl);
  const styles = getStyles(theme);

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    onEdit(rowId);
    handleClose();
  };

  const handleClone = () => {
    onClone(rowId);
    handleClose();
  };

  const handleDelete = () => {
    onDelete(rowId);
    handleClose();
  };

  return (
      <>
        <IconButton onClick={handleClick} size="small">
          <i className="bhi-kebab-menu" css={styles.iconButton} />
        </IconButton>
        <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right"
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right"
            }}
        >
          <MenuItem sx={{fontSize: 13, padding: "0 0.5rem"}} onClick={handleEdit}>
            <i className="bhi-edit" style={{fontSize: 12, marginRight: 6}} />
            Edit
          </MenuItem>
          <MenuItem sx={{fontSize: 13, padding: "0 0.5rem"}} onClick={handleClone}>
            <i className="bhi-duplicate" style={{fontSize: 12, marginRight: 6}} />
            Clone
          </MenuItem>
          <MenuItem sx={{fontSize: 13, padding: "0 0.5rem"}} onClick={handleDelete}>
            <i className="bhi-delete" style={{fontSize: 12, marginRight: 6}} />
            Delete
          </MenuItem>
        </Menu>
      </>
  );
}

function ScheduledReportsTable() {
  const [idToScheduledReport, setIdToScheduledReport] = useState(Immutable.Map());
  const [dashboards, setDashboards] = useState(Immutable.List());
  const [scheduleConfigId, setScheduleConfigId] = useState(null);
  const [scheduleIdToEdit, setScheduleIdToEdit] = useState(null);
  const [cloneFromId, setCloneFromId] = useState(null);
  const [scheduleIdToDelete, setScheduleIdToDelete] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [addNewMode, setAddNewMode] = useState(false);
  const [containerHeight, setContainerHeight] = useState(600);
  const [isLoading, setIsLoading] = useState(true);

  const scheduleContainerRef = useRef(null);

  const idToDashboard = useMemo(() => indexBy((dashboard) => dashboard.get("id"), dashboards), [dashboards]);

  const {theme} = useContext(CustomThemeContext);
  const styles = getStyles(theme);

  useEffect(() => {
    Promise.all([
      ScheduledReports.getAllDashboardsForScheduledReportAdmin(),
      ScheduledReports.getAllScheduledReportsForAdmin()
    ]).then(([dashboards, scheduledReports]) => {
      setDashboards(dashboards);
      setIdToScheduledReport(indexBy((report) => report.get("id"), scheduledReports));
      setIsLoading(false);
    });
    Auditor.audit("scheduled-reports-admin:loaded");
  }, []);

  useEffect(() => {
    const updateContainerHeight = () => {
      if (scheduleContainerRef.current) {
        setContainerHeight(scheduleContainerRef.current.clientHeight);
      }
    };

    updateContainerHeight();
    window.addEventListener("resize", updateContainerHeight);

    return () => {
      window.removeEventListener("resize", updateContainerHeight);
    };
  }, []);

  const dashboardNameById = (id) => {
    const dashboard = idToDashboard.get(id);
    return dashboard ? dashboard.get("name") : "Unknown Dashboard";
  };

  const handleEditSchedule = useCallback((scheduleId) => {
    const schedule = idToScheduledReport.get(scheduleId);
    if (schedule) {
      setScheduleConfigId(schedule.get("dashboardId"));
      setScheduleIdToEdit(scheduleId);
      setCloneFromId(null);
      setAddNewMode(false);
    }
  }, [idToScheduledReport]);

  const handleCloneSchedule = useCallback((scheduleId) => {
    const schedule = idToScheduledReport.get(scheduleId);
    if (schedule) {
      setScheduleConfigId(schedule.get("dashboardId"));
      setCloneFromId(scheduleId);
      setScheduleIdToEdit(null);
      setAddNewMode(false);

      Auditor.audit("schedule-report:cloned", {
        dashboardId: schedule.get("dashboardId"), originalName: schedule.get("name")
      });
    }
  }, [idToScheduledReport]);

  const handleDeleteSchedule = useCallback((scheduleId) => {
    setScheduleIdToDelete(scheduleId);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (scheduleIdToDelete) {
      const schedule = idToScheduledReport.get(scheduleIdToDelete);
      ScheduledReports.deleteScheduledReport(scheduleIdToDelete, schedule).then(() => {
        setIdToScheduledReport(idToScheduledReport.filter(schedule => schedule.get("id") !== scheduleIdToDelete));
        setScheduleIdToDelete(null);
        Auditor.audit("schedule-report:deleted", {
          dashboardId: schedule.get("dashboardId"),
          name: schedule.get("name")
        });
        Popups.success(
            "Successfully deleted scheduled report!",
            "top-right"
        );
      }).catch((error) => {
        console.error("Failed to delete scheduled report:", error);
        Popups.error(
            "Failed to delete scheduled report!",
            "top-right"
        );
      });
    }
  }, [scheduleIdToDelete, idToScheduledReport]);

  const handleCloneFromDrawer = useCallback(() => {
    setCloneFromId(null);
  }, []);

  const handleDeleteFromDrawer = useCallback((scheduleId) => {
    const updatedReports = idToScheduledReport.filter(schedule => schedule.get("id") !== scheduleId);
    setIdToScheduledReport(updatedReports);
  }, [idToScheduledReport]);

  const closeScheduleDrawer = useCallback(() => {
    setScheduleConfigId(null);
    setScheduleIdToEdit(null);
    setCloneFromId(null);
    setAddNewMode(false);
  }, []);

  const handleScheduleUpdate = useCallback((schedule) => {
    setIdToScheduledReport(idToScheduledReport => idToScheduledReport.set(schedule.get("id"), schedule));
    setCloneFromId(null);
  }, []);

  const onCellClick = useCallback(
      (cellValue, rowArray, columnIndex) => {
        // Only allow clicking on the schedule name column (column 0)
        if (columnIndex === 0) {
          const actionsColumnIndex = rowArray.findIndex((cell) => cell.columnName === "actions");
          const reportId = rowArray[actionsColumnIndex]?.originalValue;
          if (reportId) {
            handleEditSchedule(reportId);
          }
        }
      },
      [handleEditSchedule]
  );

  const columns = [
    {
      label: "Schedule Name",
      flexGrow: 4,
      titleMapper: (displayValue, originalValue) => originalValue,
      displayMapper: (value) => (
          <span
              style={{
                color: theme.palette.primary.main,
                cursor: "pointer"
              }}
          >
            {value}
          </span>
      )
    },
    {label: "Report Source", flexGrow: 2},
    {label: "Owner", flexGrow: 1},
    {label: "Status", flexGrow: 1},
    {label: "Recurring Schedule", flexGrow: 1},
    {
      label: "Next Schedule",
      flexGrow: 1,
      displayMapper: (value) => {
        if (!value || !value.date) return "-";
        return value.timezone
            ? formatDateTimeInTimezone(value.date, value.timezone)
            : formatDateTime(value.date);
      },
      sortMapper: (value) => (value && value.date ? new Date(value.date).getTime() : 0)
    },
    {
      label: "Created Date",
      flexGrow: 1,
      displayMapper: (value) => formatDateTime(value),
      sortMapper: (value) => (value ? new Date(value).getTime() : 0)
    },
    {
      label: "Last Modified",
      flexGrow: 1,
      displayMapper: (value) => formatDateTime(value),
      sortMapper: (value) => (value ? new Date(value).getTime() : 0)
    },
    {
      label: "Last Sent",
      flexGrow: 1,
      displayMapper: (value) => value === "FAILED" ? "Failed" : formatDateTime(value),
      sortMapper: (value) => value === "FAILED" ? 0 : new Date(value).getTime()
    },
    {
      label: "Actions",
      flexGrow: 0,
      width: 60,
      disableSort: true,
      fixedRight: true,
      displayMapper: (value, originalValue) => {
        return <ActionMenu
            rowId={originalValue}
            onEdit={handleEditSchedule}
            onClone={handleCloneSchedule}
            onDelete={handleDeleteSchedule} />;
      }
    }
  ];

  const reportToRow = (report) => {
    const lastRunDate = report.get("lastRunDateTimeUtc");
    const lastStatus = report.get("lastRunStatus");
    const lastSent = lastStatus === "FAILED" ? "FAILED" : lastRunDate;
    const ownerId = report.get("ownerId");
    const user = ownerId ? Users.getUser(ownerId) : null;
    const ownerName = user ? user.get("fullName") : "Unknown";
    const nextScheduleValue = getNextScheduledRun(report);
    const nextScheduleTz = report.get("timezone");
    return [
      report.get("name"),
      dashboardNameById(report.get("dashboardId")),
      ownerName,
      "Active",
      getRecurrenceText(report.get("recurrenceSchedule")),
      {date: nextScheduleValue, timezone: nextScheduleTz},
      report.get("createdDateTimeUtc"),
      report.get("updatedDateTimeUtc"),
      lastSent,
      report.get("id")
    ];
  };

  const filteredReports = idToScheduledReport.valueSeq().filter((report) => {
    if (!searchText) {
      return true;
    }
    const searchLower = searchText.toLowerCase();
    const name = report.get("name", "").toLowerCase();
    const dashboardName = dashboardNameById(report.get("dashboardId")).toLowerCase();
    return name.includes(searchLower) || dashboardName.includes(searchLower);
  });

  const rows = filteredReports.map(reportToRow).toJS();

  return (
      <>
        <div ref={scheduleContainerRef} style={{maxWidth: "100%", margin: "0 auto", height: "calc(100vh - 170px)"}}>
          {isLoading ? (
              <LoadingSpinner />
          ) : (
              <>
                <div css={styles.searchContainer}>
                  <div css={styles.searchInput}>
                    <i className="bhi-search" />
                    <input value={searchText} placeholder="Search..." onChange={(e) => setSearchText(e.target.value)} />
                  </div>
                </div>

                <SimpleDataTable
                    id="scheduled-reports-table"
                    columns={columns}
                    rows={rows}
                    onCellClick={onCellClick}
                    initialSortDirection="ASC"
                    initialSortBy={0}
                    maxTableHeight={containerHeight}
                    downloadable={false}
                    searchable={false}
                />
              </>
          )}
        </div>

        {!dashboards.isEmpty() && (
            <ScheduleDrawer
                open={scheduleConfigId !== null}
                onRequestClose={closeScheduleDrawer}
                configId={scheduleConfigId}
                scheduleToEdit={scheduleIdToEdit ? idToScheduledReport.get(scheduleIdToEdit) : null}
                cloneFromId={cloneFromId}
                idToDashboard={idToDashboard}
                SavedConfigs={SavedConfigs}
                onScheduleUpdate={handleScheduleUpdate}
                onScheduleDelete={handleDeleteFromDrawer}
                onCloneClick={handleCloneFromDrawer}
                addNewMode={addNewMode}
                existingSchedules={idToScheduledReport.valueSeq()}
                idToScheduledReport={idToScheduledReport}
            />
        )}

        {scheduleIdToDelete !== null && <Dialog>
          <div style={{width: "600px", textAlign: "center"}}>
            <svg width="58" height="58" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#F59E0B" strokeWidth="2" fill="none" />
              <line x1="12" y1="8" x2="12" y2="12" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" />
              <circle cx="12" cy="16" r="1" fill="#F59E0B" />
            </svg>
            <p style={{fontSize: "1.6rem", fontWeight: "500", marginBottom: 0}}>
              Are you sure you want to delete this report permanently?
            </p>
            <p style={{fontSize: "1.2rem"}}>
              Once you delete you will not be able to undo this action.
            </p>
            <hr />

            <div style={{display: "flex", gap: "10px", marginTop: "20px", justifyContent: "end"}}>
              <TextButton
                  label="Cancel"
                  onClick={() => setScheduleIdToDelete(null)} />
              <TextButton
                  testId="confirm-delete-report-button"
                  icon="trash"
                  type="alert"
                  label="Delete"
                  onClick={handleConfirmDelete} />
            </div>
          </div>
        </Dialog>}
      </>
  );
}

export default ScheduledReportsTable;
